import { computed } from 'vue';
import { enFetch } from 'Js/utils/enFetch';
import { useStateStore } from 'StoresSpa/state';
import { ColumnSortDirections } from '../types/columnSortDirection';
import VarType from 'Js/enums/varType';
const columnStateUrlParam = 'columnState';
/**
 * Name of the query parameter a datagrid saves its view in. Every datagrid gets its own,
 * so two datagrids on one page do not overwrite each other.
 */
export function buildColumnStateUrlParam(staticId) {
    return staticId ? `${columnStateUrlParam}-${staticId}` : columnStateUrlParam;
}
/**
 * Takes 'asc' and 'desc' as they are and turns anything else into null, which stands for a column
 * that is not sorted. Sort directions arrive from the url, so they can hold any value at all.
 */
function toSortDirection(sort) {
    return Object.values(ColumnSortDirections).includes(sort) ? sort : null;
}
/**
 * Columns that need a saved position, because they are no longer where the module put them.
 *
 * Dragging one column to the front pushes every other column back by one, so simply comparing
 * positions would mark the whole datagrid as reordered. Instead we look for the longest group of
 * columns that are still in their default order among themselves - those can stay as they are, and
 * only the rest needs saving.
 */
function findReorderedColumns(defaultOrder, order) {
    // Every displayed column with the position the module gave it. A column the module does not know
    // has no place to stay at, so it is left out and counts as reordered.
    const positionedColumns = order
        .map(dataField => ({ dataField, defaultIndex: defaultOrder.indexOf(dataField) }))
        .filter(column => column.defaultIndex >= 0);
    // Longest group whose default order is still intact (a longest increasing subsequence).
    const runLength = [];
    const predecessor = [];
    let longestRunEnd = -1;
    positionedColumns.forEach((column, index) => {
        runLength[index] = 1;
        predecessor[index] = -1;
        for (let earlier = 0; earlier < index; earlier++) {
            if (positionedColumns[earlier].defaultIndex < column.defaultIndex
                && runLength[earlier] + 1 > runLength[index]) {
                runLength[index] = runLength[earlier] + 1;
                predecessor[index] = earlier;
            }
        }
        if (longestRunEnd < 0 || runLength[index] > runLength[longestRunEnd])
            longestRunEnd = index;
    });
    const stayingColumns = new Set();
    for (let index = longestRunEnd; index >= 0; index = predecessor[index]) {
        stayingColumns.add(positionedColumns[index].dataField);
    }
    return new Set(order.filter(dataField => !stayingColumns.has(dataField)));
}
/**
 * Works out what the user changed about the module's view. Only that gets saved.
 */
export function diffColumnState(defaultState, state) {
    const defaults = new Map(defaultState.map(entry => [entry.dataField, entry]));
    const reorderedColumns = findReorderedColumns(defaultState.map(entry => entry.dataField), state.map(entry => entry.dataField));
    const changes = [];
    state.forEach(entry => {
        const change = { dataField: entry.dataField };
        const defaultEntry = defaults.get(entry.dataField);
        let differs = false;
        if (entry.isVisible !== (defaultEntry?.isVisible ?? true)) {
            change.isVisible = entry.isVisible;
            differs = true;
        }
        if ((entry.sort ?? null) !== (defaultEntry?.sort ?? null)) {
            change.sort = entry.sort ?? null;
            differs = true;
        }
        if (reorderedColumns.has(entry.dataField)) {
            change.index = entry.index;
            differs = true;
        }
        if (differs)
            changes.push(change);
    });
    return changes;
}
/**
 * Builds the whole view back up from the module's view plus the saved changes.
 *
 * Columns the datagrid no longer has are skipped, a column listed twice counts once, and an index past
 * the last column puts it at the end. The rebuilt view is always numbered 0, 1, 2 ... no matter
 * which indexes were saved.
 */
export function applyColumnStateChanges(defaultState, changes) {
    const defaults = new Map(defaultState.map(entry => [entry.dataField, entry]));
    const changesByDataField = new Map();
    (changes ?? []).forEach(change => {
        if (!change?.dataField || !defaults.has(change.dataField))
            return;
        if (changesByDataField.has(change.dataField))
            return;
        changesByDataField.set(change.dataField, change);
    });
    const reorderedChanges = [...changesByDataField.values()].filter(change => Number.isInteger(change.index));
    // Lowest index first, so everything in front of a column is already in place when it is inserted.
    reorderedChanges.sort((first, second) => first.index - second.index);
    const reorderedColumns = new Set(reorderedChanges.map(change => change.dataField));
    const order = defaultState
        .map(entry => entry.dataField)
        .filter(dataField => !reorderedColumns.has(dataField));
    reorderedChanges.forEach(change => {
        const index = Math.min(Math.max(change.index, 0), order.length);
        order.splice(index, 0, change.dataField);
    });
    return order.map((dataField, index) => {
        const defaultEntry = defaults.get(dataField);
        const change = changesByDataField.get(dataField);
        return {
            dataField,
            isVisible: change?.isVisible ?? defaultEntry.isVisible,
            index,
            sort: change && 'sort' in change ? change.sort ?? null : defaultEntry.sort ?? null,
        };
    });
}
function parseJson(raw) {
    if (!raw)
        return null;
    try {
        return JSON.parse(raw);
    }
    catch (_) {
        return null;
    }
}
/**
 * Keeps what looks like saved changes and drops the rest, so a hand-edited url or an older format in
 * the database shows the default view instead of breaking the datagrid.
 */
function toColumnStateChanges(entries) {
    if (!Array.isArray(entries))
        return [];
    return entries
        .filter(entry => typeof entry?.dataField === 'string')
        .map(entry => {
        const change = { dataField: entry.dataField };
        if (typeof entry.isVisible === 'boolean')
            change.isVisible = entry.isVisible;
        if (Number.isInteger(entry.index))
            change.index = entry.index;
        // "sort" without a direction means the user cleared the module's sorting.
        if ('sort' in entry)
            change.sort = toSortDirection(entry.sort);
        return change;
    });
}
/**
 * Reads the saved changes out of the url.
 */
export function parseColumnStateChanges(rawChanges) {
    return toColumnStateChanges(parseJson(rawChanges));
}
/**
 * Turns the saved changes into the value of the query parameter. Counterpart of the reading above,
 * so the format stays in one place.
 */
export function serializeColumnStateChanges(changes) {
    return JSON.stringify(changes);
}
/**
 * Reads what the server stored for a module. A module can show more than one data grid - the rental
 * data has one per tab - so the views are kept under the id of the grid they belong to.
 */
export function parseDatagridState(rawState) {
    const parsed = parseJson(rawState);
    if (!parsed || typeof parsed !== VarType.Object || Array.isArray(parsed))
        return {};
    return Object.fromEntries(Object.entries(parsed)
        .map(([grid, changes]) => [grid, toColumnStateChanges(changes)]));
}
/**
 * A datagrid's view: which columns are shown, in which order, and how they are sorted. It is saved
 * on every change and restored the next time the datagrid is opened.
 *
 * Only the user's changes are saved, not the whole list of columns. That keeps the url short, and a
 * column added to the module later simply shows up for everyone.
 */
export function useColumnState({ headers, getSortBy, setSortBy, staticId, moduleName, saveStateInUrl = true }) {
    const urlParam = buildColumnStateUrlParam(staticId);
    // The name this grid's view is stored under within its module.
    const gridKey = staticId ?? 'datagrid';
    function currentState() {
        const sortBy = getSortBy() ?? [];
        return headers.value
            .filter(header => !!header.value)
            .map((header, index) => ({
            dataField: header.value,
            isVisible: !header.isHidden,
            index,
            sort: sortBy.find(sorted => sorted.key === header.value)?.order ?? null,
        }));
    }
    /**
     * The view as it is displayed right now.
     */
    const columnState = computed(currentState);
    /**
     * The module's own view, taken before a saved one is applied. This is what "restore default" goes back to.
     * A module may rebuild its columns later, e.g. once its configuration arrived, which makes a new default.
     */
    let defaultColumnState = currentState();
    /**
     * What the user changed. This is what gets saved.
     */
    const columnStateChanges = computed(() => diffColumnState(defaultColumnState, columnState.value));
    function findHeader(dataField) {
        return headers.value.find(header => header.value === dataField);
    }
    /**
     * Displays the named columns in the given order. Columns that are not named keep their place, so
     * hidden and fixed columns stay where they are.
     */
    function applyColumnOrder(orderedDataFields) {
        const reordered = orderedDataFields
            .map(dataField => findHeader(dataField))
            .filter(header => !!header);
        if (reordered.length === 0)
            return;
        let nextPosition = 0;
        headers.value = headers.value.map(header => reordered.includes(header) ? reordered[nextPosition++] : header);
    }
    /**
     * Displays a whole view: order, visibility and sorting.
     */
    function applyColumnState(state) {
        applyColumnOrder(state.map(entry => entry.dataField));
        state.forEach(entry => {
            const header = findHeader(entry.dataField);
            // A column the user cannot hide keeps whatever the module decided.
            if (header?.canBeToggled)
                header.isHidden = !entry.isVisible;
        });
        setSortBy(state
            .filter(entry => !!entry.sort)
            .map(entry => ({ key: entry.dataField, order: entry.sort })));
    }
    /**
     * True while nothing was changed. An untouched datagrid should not leave anything in the url.
     */
    function isDefaultView() {
        return columnStateChanges.value.length === 0;
    }
    /**
     * The view as a query parameter. The filters rebuild the whole query string, so they need this to
     * hand the view back instead of dropping it.
     */
    function toUrlParams() {
        if (!saveStateInUrl || isDefaultView())
            return {};
        return { [urlParam]: serializeColumnStateChanges(columnStateChanges.value) };
    }
    function replaceUrl(url) {
        // Already the url we want - writing it again would only add a history entry.
        if (window.location.href === url.href)
            return;
        window.history.pushState({}, '', url.href);
    }
    /**
     * Puts the view into the url, or takes it out again once the user is back to the module's view.
     * Only this datagrid's parameter is touched, so filters and other grids keep theirs.
     */
    function writeColumnStateToUrl() {
        if (!saveStateInUrl)
            return;
        const url = new URL(window.location.href);
        if (isDefaultView()) {
            url.searchParams.delete(urlParam);
        }
        else {
            url.searchParams.set(urlParam, serializeColumnStateChanges(columnStateChanges.value));
        }
        replaceUrl(url);
    }
    /**
     * The views the server saved for this user and module, one per data grid. Read from the module
     * state, which is fetched per user on every navigation.
     */
    function savedDatagridState() {
        return parseDatagridState(useStateStore().moduleState?.UserSettings?.DatagridState);
    }
    /**
     * Keeps the module state in step with what was just saved. The configuration is only fetched again
     * when the user leaves the module, and a grid that is opened before that reads it from here.
     */
    function rememberDatagridState(state) {
        const moduleState = useStateStore().moduleState;
        if (!moduleState)
            return;
        moduleState.UserSettings = { ...moduleState.UserSettings, DatagridState: state };
    }
    /**
     * The changes the server keeps.
     * The sorting is left out intentionally until the product owner decided whether a
     * saved view should bring it back, so a column that was only sorted is dropped here.
     */
    function changesToSaveOnServer() {
        return columnStateChanges.value
            .map(({ sort, ...change }) => change)
            .filter(change => change.isVisible !== undefined || change.index !== undefined);
    }
    /**
     * Saves the view for this user and module.
     */
    function saveColumnStateOnServer() {
        if (!moduleName)
            return;
        // The server replaces the module's whole state, so the views of its other grids are read
        // here and sent back along with this one.
        const states = savedDatagridState();
        const changes = changesToSaveOnServer();
        if (changes.length === 0) {
            delete states[gridKey];
        }
        else {
            states[gridKey] = changes;
        }
        const datagridState = Object.keys(states).length === 0 ? null : JSON.stringify(states);
        rememberDatagridState(datagridState);
        enFetch('/api/Configuration/SaveUserSettings', {
            method: 'POST',
            noResponseBody: true,
            body: JSON.stringify({
                moduleName: moduleName,
                datagridState: datagridState,
            }),
        }).catch(error => console.error('Failed to save column state', error));
    }
    /**
     * Loads the saved view when the datagrid is created. Returns true if there was one.
     */
    function loadColumnState() {
        // Inside a module the server is the only source. A url can outlive a session - the login sends
        // the user back to the page they came from, query string and all - so reading it would show one
        // user the view of the one before. Without a module there is no saved view and the url is all
        // there is.
        const changes = moduleName
            ? savedDatagridState()[gridKey] ?? []
            : saveStateInUrl
                ? parseColumnStateChanges(new URLSearchParams(window.location.search).get(urlParam))
                : [];
        if (changes.length === 0)
            return false;
        applyColumnState(applyColumnStateChanges(defaultColumnState, changes));
        return true;
    }
    /**
     * Takes the module's columns as the new default and puts the saved view back on top of them.
     * Called when the module rebuilt its columns, which would otherwise drop the view that was loaded.
     */
    function reloadColumnState() {
        defaultColumnState = currentState();
        return loadColumnState();
    }
    /**
     * Saves the view. Called whenever a column is hidden, shown, reordered or sorted.
     */
    function saveColumnState() {
        writeColumnStateToUrl();
        saveColumnStateOnServer();
    }
    /**
     * Back to the module's view, and the saved one is gone from the url.
     */
    function restoreDefaultColumnState() {
        applyColumnState(defaultColumnState);
        writeColumnStateToUrl();
        saveColumnStateOnServer();
    }
    return {
        columnState,
        isDefaultView,
        applyColumnOrder,
        loadColumnState,
        reloadColumnState,
        saveColumnState,
        restoreDefaultColumnState,
        toUrlParams,
    };
}
